import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest } from 'rxjs';
import { EntityDocumentsService, HorizontalListData, LoanDocumentService, SignatureMethod } from 'common';
import { DocuSignEnvelopeService } from './docusign-envelope.service';
import { SigningWidgetAdapter } from './signing-widget.data';
import { ApplicationEnvelopeData, SignatureMethodSwitchOption } from './signing-widget.model';
import { readOnly } from '../../user/user-permission/user-permission.data';
import { UserPermissionService } from '../../user/user-permission/user-permission.service';

/** Signature method and the current DocuSign envelope, with the executed documents it produced. */
@UntilDestroy()
@Component({
  selector: 'ifb-signing-widget',
  templateUrl: './signing-widget.component.html',
  styleUrls: ['./signing-widget.component.scss'],
  providers: [SigningWidgetAdapter],
  standalone: false
})
export class SigningWidgetComponent implements OnInit, OnChanges {
  @Input() signatureMethod: SignatureMethod;
  @Input() envelope: ApplicationEnvelopeData;
  @Input() switchOptions: SignatureMethodSwitchOption[] = [];
  @Output() switchMethod = new EventEmitter<SignatureMethod>();

  listData: HorizontalListData[];

  // A signed loan agreement is a loan document and an executed MSA an entity document; each API gates its own.
  private canReadLoanDocuments = false;
  private canReadEntityDocuments = false;

  constructor(
    private readonly adapter: SigningWidgetAdapter,
    private readonly envelopeService: DocuSignEnvelopeService,
    private readonly loanDocumentService: LoanDocumentService,
    private readonly entityDocumentsService: EntityDocumentsService,
    private readonly userPermissionService: UserPermissionService,
  ) { }

  ngOnInit(): void {
    combineLatest([
      this.userPermissionService.granted([readOnly('servicing-account-documents')]),
      this.userPermissionService.granted([readOnly('servicing-entity-documents')]),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([canReadLoanDocuments, canReadEntityDocuments]: [boolean, boolean]) => {
        this.canReadLoanDocuments = canReadLoanDocuments;
        this.canReadEntityDocuments = canReadEntityDocuments;
        this.refreshListData();
      });
  }

  ngOnChanges(): void {
    this.refreshListData();
  }

  downloadSignedAgreement(): void {
    if (this.envelope.executedEntityDocumentId) {
      this.entityDocumentsService.download(this.envelope.executedEntityDocumentId);
      return;
    }

    this.loanDocumentService.download(this.envelope.executedDocumentId, this.envelope.loanId);
  }

  downloadCertificate(): void {
    this.envelopeService.downloadCertificate(this.envelope.envelopeId);
  }

  trackBySwitchTarget(_index: number, option: SignatureMethodSwitchOption): SignatureMethod {
    return option.target;
  }

  private refreshListData(): void {
    const canReadSignedAgreement = this.envelope?.executedEntityDocumentId ? this.canReadEntityDocuments : this.canReadLoanDocuments;
    this.listData = this.adapter.adaptListData(this.signatureMethod, this.envelope, !!this.switchOptions?.length, canReadSignedAgreement);
  }
}
